import React, { useState } from 'react';
import AppIcon from '../../../../components/AppIcon';
import type { IconName } from '../../../../components/AppIcon';

interface ToolMetric {
  label: string;
  value: string;
  sub: string;
  trend: string;
  up: boolean;
}

interface ToolData {
  name: string;
  icon: IconName;
  color: string;
  bg: string;
  textColor: string;
  metrics: ToolMetric[];
  insights: string[];
}

const toolsData: Record<string, ToolData> = {
  taskManagement: {
    name: 'Jira / Asana',
    icon: 'Kanban',
    color: 'from-blue-500 to-cyan-500',
    bg: 'bg-blue-50',
    textColor: 'text-blue-700',
    metrics: [
      { label: 'Tasks Completed', value: '47', sub: 'this quarter', trend: '+12%', up: true },
      { label: 'On-time Delivery', value: '89%', sub: 'of assigned tasks', trend: '+5%', up: true },
      { label: 'Avg Completion Time', value: '2.3d', sub: 'per task', trend: '-0.4d', up: true },
      { label: 'Overdue Tasks', value: '3', sub: 'currently open', trend: '-2', up: true },
    ],
    insights: [
      'Consistently closes sprint tasks ahead of deadline',
      'High contribution to cross-team projects (8 this quarter)',
      'Preferred task type: Backend feature development',
    ],
  },
  crm: {
    name: 'Salesforce CRM',
    icon: 'Users',
    color: 'from-sky-500 to-blue-600',
    bg: 'bg-sky-50',
    textColor: 'text-sky-700',
    metrics: [
      { label: 'Client Interactions', value: '124', sub: 'this quarter', trend: '+18%', up: true },
      { label: 'Deal Contributions', value: '6', sub: 'deals influenced', trend: '+2', up: true },
      { label: 'Response Rate', value: '94%', sub: 'to client queries', trend: '+3%', up: true },
      { label: 'Satisfaction Score', value: '4.7/5', sub: 'avg client rating', trend: '+0.2', up: true },
    ],
    insights: [
      'Strong relationship-building with enterprise accounts',
      'Proactive follow-up cadence above team average',
      'Key contributor to Q4 renewal pipeline',
    ],
  },
  officeSuite: {
    name: 'Google Workspace / M365',
    icon: 'FileText',
    color: 'from-emerald-500 to-teal-500',
    bg: 'bg-emerald-50',
    textColor: 'text-emerald-700',
    metrics: [
      { label: 'Docs Collaborated', value: '38', sub: 'shared documents', trend: '+9', up: true },
      { label: 'Meetings Attended', value: '52', sub: 'this quarter', trend: '-4', up: false },
      { label: 'Meeting Contribution', value: '73%', sub: 'active participation', trend: '+8%', up: true },
      { label: 'Async Responses', value: '96%', sub: 'reply rate', trend: '+2%', up: true },
    ],
    insights: [
      'High document co-authoring activity across departments',
      'Reduced meeting load while maintaining collaboration quality',
      'Strong async communication patterns',
    ],
  },
};

const ExternalIntegrationsPanel: React.FC = () => {
  const [activeTab, setActiveTab] = useState<string>('taskManagement');
  const tool = toolsData?.[activeTab];

  return (
    <div className="bg-card border border-border rounded-xl p-6">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-9 h-9 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-lg flex items-center justify-center">
          <AppIcon name="Plug" size={18} color="white" />
        </div>
        <div>
          <h3 className="font-semibold text-foreground text-base">External Tool Integrations</h3>
          <p className="text-xs text-muted-foreground">Insights from connected productivity platforms</p>
        </div>
        <span className="ml-auto text-xs bg-amber-100 text-amber-700 px-2.5 py-1 rounded-full font-medium flex items-center gap-1">
          <span className="w-1.5 h-1.5 bg-amber-500 rounded-full" />
          Mock Data
        </span>
      </div>
      {/* Tab Switcher */}
      <div className="flex gap-2 mb-5 bg-muted/40 p-1 rounded-lg">
        {Object.entries(toolsData)?.map(([key, t]) => (
          <button
            key={key}
            onClick={() => setActiveTab(key)}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-md text-xs font-medium transition-all ${
              activeTab === key
                ? 'bg-white shadow-sm text-foreground'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <AppIcon name={t?.icon} size={13} />
            <span className="hidden sm:inline">{t?.name?.split(' ')?.[0]}</span>
          </button>
        ))}
      </div>
      {/* Tool Header */}
      <div className={`flex items-center gap-2 mb-4 ${tool?.bg} rounded-lg px-4 py-2.5`}>
        <div className={`w-7 h-7 bg-gradient-to-br ${tool?.color} rounded-md flex items-center justify-center`}>
          <AppIcon name={tool?.icon} size={14} color="white" />
        </div>
        <span className={`text-sm font-semibold ${tool?.textColor}`}>{tool?.name}</span>
      </div>
      {/* Metrics Grid */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        {tool?.metrics?.map((m) => (
          <div key={m?.label} className="bg-muted/30 rounded-lg p-3">
            <div className="text-xl font-bold text-foreground">{m?.value}</div>
            <div className="text-xs text-muted-foreground">{m?.label}</div>
            <div className="text-xs text-muted-foreground">{m?.sub}</div>
            <div className={`text-xs font-medium mt-1 flex items-center gap-1 ${m?.up ? 'text-emerald-600' : 'text-rose-500'}`}>
              <AppIcon name={m?.up ? 'TrendingUp' : 'TrendingDown'} size={11} />
              {m?.trend} vs last quarter
            </div>
          </div>
        ))}
      </div>
      {/* Insights */}
      <div className="border-t border-border pt-3">
        <p className="text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wide">Key Insights</p>
        <div className="space-y-1.5">
          {tool?.insights?.map((insight) => (
            <div key={insight} className="flex items-start gap-2 text-xs text-foreground">
              <AppIcon name="Sparkles" size={12} className="text-violet-500 mt-0.5 flex-shrink-0" />
              {insight}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ExternalIntegrationsPanel;
